import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { HeadingText } from '../shared/HeadingText';
import { AddButton } from '../shared/AddButton';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { tierIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { euro, rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { fill } from '../../utils/copy';
import { belowHeader, eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnIcon, btnOnClay, btnPrice, btnPriceAmount, btnPriceLabel, chipLgCls } from '../../ui/styles';
import { capList } from './homeRules';
import { pageActions } from '../../data/pageActions';
import type { Concern, Routine } from '../../core/types/catalog';
import type { HomePage } from '../../core/types/pages';

/** Ruimte tussen de panelen (Tailwind `gap-6`). */
const GAP = 24;

/**
 * "Waar loopt jouw huid tegenaan?" (stap Kiezen): per klacht beide kanten van Beauty Bewust.
 * Links wat je thuis gebruikt (het protocol, los of in één keer in de mand), rechts wat er in de
 * salon bij past.
 *
 * De klachten zijn tabs bovenin (blijven vast staan zolang je in het blok bent); de adviezen
 * staan als panelen naast elkaar en klikken per paneel vast. Swipen (telefoon, tablet,
 * trackpad) of op een pil tikken: allebei schuiven het paneel en de pil mee. Geen half
 * zichtbare buren: de panelen zijn hoog. De Thuis-lijst is hoogstens vier regels, dus alle
 * panelen zijn even hoog en niets verspringt. Pijltjestoetsen wisselen ook.
 */
type Props = {
  /** De woorden boven de kiezer; standaard die van Home. Protocollen geeft eigen woorden mee. */
  copy?: Pick<HomePage['concerns'], 'eyebrow' | 'heading' | 'body' | 'quizLine'>;
  /**
   * Rustig (Protocollen): geen fade-in en geen glijdende pil. Home blijft speels. Het schuiven
   * tussen panelen blijft, want dat is bediening, geen versiering.
   */
  still?: boolean;
  /** Onder de panelen, op hetzelfde vlak (Protocollen: vergelijken en klein beginnen). */
  children?: ReactNode;
};
export function ConcernFinder({
  copy: ownCopy,
  still = false,
  children
}: Props = {}) {
  const pages = usePages();
  const {
    concerns,
    routineForConcern,
    shopRating
  } = useCatalog();
  const {
    openQuiz
  } = useOverlayActions();
  const reduceMotion = useReducedMotion();
  const reduce = reduceMotion || still;
  const baseId = useId();
  const copy = ownCopy ?? pages.home.concerns;
  const [active, setActive] = useState(0);

  // Alleen klachten met een protocol: zonder is er niets te kiezen (lege backend).
  const items = concerns.flatMap(c => {
    const routine = routineForConcern(c);
    return routine ? [{
      concern: c,
      routine
    }] : [];
  });
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const pillRowRef = useRef<HTMLUListElement>(null);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const overflow = usePillOverflow(pillRowRef, items.length);

  // De actieve pil altijd in beeld in zijn rij (alleen horizontaal: de pagina scrolt niet mee).
  useEffect(() => {
    const row = pillRowRef.current;
    const pill = pillRefs.current[active];
    if (!row || !pill) return;
    const left = pill.offsetLeft - row.clientWidth / 2 + pill.clientWidth / 2;
    row.scrollTo({
      left: Math.max(0, left),
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [active, reduce]);
  if (items.length === 0) return null;

  /** Na swipen: welk paneel staat er nu? */
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const next = Math.round(track.scrollLeft / (track.clientWidth + GAP));
    if (next !== active && next >= 0 && next < items.length) setActive(next);
  };

  /** Pil, pijl of toets: naar paneel `i`. Viel het advies onder de vaste balk, dan terug naar boven. */
  const choose = (i: number, focus = false) => {
    const index = Math.max(0, Math.min(items.length - 1, i));
    setActive(index);
    const track = trackRef.current;
    if (track) track.scrollTo({
      left: index * (track.clientWidth + GAP),
      behavior: reduce ? 'auto' : 'smooth'
    });
    if (focus) pillRefs.current[index]?.focus();
    const bar = barRef.current;
    if (!bar || !track) return;
    const gap = track.getBoundingClientRect().top - bar.getBoundingClientRect().bottom;
    if (gap < -1) window.scrollBy({
      top: gap - 8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const keys: Record<string, number> = {
      ArrowRight: active + 1,
      ArrowLeft: active - 1,
      Home: 0,
      End: items.length - 1
    };
    const target = keys[e.key];
    if (target === undefined) return;
    e.preventDefault();
    choose(target, true);
  };
  return <Section tone="shell" labelledBy="concern" contained={false} data-id="element-7513" data-mp="o7kufyp3gh1w">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7514" data-mp="a94o48v58ska">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7515" data-mp="f8pis73a6er7">
          <div className="lg:col-span-7" data-id="element-7516" data-mp="f6mo39r6nxg5">
            {copy.eyebrow && <p className={`${eyebrowCls} text-clay`} data-id="element-7517" data-mp="1aj4eadxr7hx">{fill(copy.eyebrow)}</p>}
            <h2 id="concern" className={`${copy.eyebrow ? 'mt-4' : ''} ${sectionHeading} text-ink`} data-id="element-7518" data-mp="5a1iam7caib9">
              <HeadingText heading={copy.heading} data-id="element-7519" data-mp="1voadn8scrpcu" />
            </h2>
          </div>
          <div className="lg:col-span-5" data-id="element-7520" data-mp="1zpcdnru3g5fv">
            {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7521" data-mp="1zmq7nwmupxzr">{fill(copy.body)}</p>}
            {copy.quizLine && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`mt-2 ${quietLink} text-left text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-7522" data-mp="33mxx9sqaednn">
                {fill(copy.quizLine)}
              </button>}
          </div>
        </div>

        {/* De klachten blijven net onder de header staan zolang je door dit blok scrolt. */}
        <div ref={barRef} className={`sticky ${belowHeader} z-sticky -mx-6 mt-10 flex items-center gap-2 bg-shell py-3 lg:-mx-10 lg:px-10`} data-id="element-7523" data-mp="3nbgtuvdmgtcd">
          <ul ref={pillRowRef} role="tablist" aria-label="Kies je huidklacht" onKeyDown={onKeyDown} className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-px-6 px-6 pb-1 [scrollbar-width:none] lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7524" data-mp="3dl3ye849rfrg">
            {items.map(({
            concern
          }, i) => {
            const isActive = i === active;
            return <li key={concern.id} role="presentation" className="shrink-0" data-id="element-7525" data-mp="mfftcubzyij3" data-mpi={i}>
                  <button ref={el => {
                pillRefs.current[i] = el;
              }} type="button" role="tab" id={`${baseId}-tab-${i}`} aria-selected={isActive} aria-controls={`${baseId}-panel-${i}`} tabIndex={isActive ? 0 : -1} onClick={() => choose(i)} className={chipLgCls(isActive)} data-id="element-7526" data-mp="rcy5etlb9dvb">
                    {isActive && (still ? <span aria-hidden="true" className="absolute inset-[-1px] rounded-full bg-ink" data-id="element-7527" data-mp="1j4nisdfqlbsa" /> : <motion.span layoutId="concern-pill" aria-hidden="true" className="absolute inset-[-1px] rounded-full bg-ink" transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7528" data-mp="1o4ryw2cqg4hz" />)}
                    <span className="relative" data-id="element-7529" data-mp="2jc37ilbqion6">{concern.label}</span>
                  </button>
                </li>;
          })}
          </ul>
          {/* Vorige/volgende alleen als de pillen niet allemaal passen (computer zonder trackpad). */}
          {overflow && <div className="hidden shrink-0 gap-1 pr-6 sm:flex lg:pr-0" data-id="element-7530" data-mp="1lqnsa4ifpxsn">
              <ArrowButton label="Vorige klacht" disabled={active === 0} onClick={() => choose(active - 1)} icon="prev" data-id="element-7531" data-mp="20f22x1m3eqjr" />
              <ArrowButton label="Volgende klacht" disabled={active === items.length - 1} onClick={() => choose(active + 1)} icon="next" data-id="element-7532" data-mp="1vlpht1gmh1vr" />
            </div>}
        </div>

        {/* Het spoor: alle panelen naast elkaar, vastklikken per paneel. Even hoog door de vaste lijst. */}
        <motion.div ref={trackRef} onScroll={onScroll} initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '0px 0px -15% 0px'
      }} transition={{
        duration: 0.3,
        ease
      }} className="mt-5 flex snap-x snap-mandatory gap-6 overflow-x-auto overscroll-x-contain [scrollbar-width:none] lg:mt-8 [&::-webkit-scrollbar]:hidden" data-id="element-7533" data-mp="3el6ly9ybj7lx">
          {items.map(({
          concern,
          routine
        }, i) => <ConcernPanel key={concern.id} id={`${baseId}-panel-${i}`} labelledBy={`${baseId}-tab-${i}`} concern={concern} routine={routine} hidden={i !== active} quietBook={still} ratingLine={shopRating.count > 0 ? `${rating(shopRating.average)} uit ${plural(shopRating.count, 'beoordeling', 'beoordelingen')}` : ''} data-id="element-7534" data-mp="65df6mtshai2" data-mpi={i} />)}
        </motion.div>

        {children}
      </div>
    </Section>;
}
type PanelProps = {
  id: string;
  labelledBy: string;
  concern: Concern;
  routine: Routine;
  hidden: boolean;
  ratingLine: string;
  quietBook: boolean;
};

/**
 * Eén advies: Thuis (het protocol) en In de salon (de behandeling of het consult).
 * `quietBook`: boeken als rustige link (Protocollen: daar is kopen de hoofdactie en staan grote
 * boekknoppen alleen in opening en slot).
 */
function ConcernPanel({
  id,
  labelledBy,
  concern,
  routine,
  hidden,
  ratingLine,
  quietBook
}: PanelProps) {
  const {
    routineTotals,
    productsForSkinType,
    treatmentForConcern
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    openBooking
  } = useOverlayActions();
  const {
    giftTiers,
    policy
  } = useSite();
  const addCta = usePages().home.concerns.addCta || 'Protocol in de mand';
  const treatment = treatmentForConcern(concern);
  const totals = routineTotals(routine);
  const matching = productsForSkinType(concern.skinType).length;
  const gift = tierIn(giftTiers, totals.price);
  const allSoldOut = isAllSoldOut(totals.items);
  const {
    shown,
    more
  } = capList(totals.items);
  return <div id={id} role="tabpanel" aria-labelledby={labelledBy} aria-hidden={hidden}
  // Niet-actieve panelen: niet bereikbaar met Tab, wel zichtbaar tijdens het swipen.
  ref={el => {
    if (!el) return;
    if (hidden) el.setAttribute('inert', '');else el.removeAttribute('inert');
  }} className="flex w-full shrink-0 snap-start flex-col" data-id="element-7535" data-mp="2f2huwrygdzm6">
      <p className="max-w-3xl text-[17px] leading-relaxed text-muted" data-id="element-7536" data-mp="2xt8zzhber3ur">{concern.description}</p>

      <div className="mt-8 grid flex-1 gap-4 lg:grid-cols-12 lg:gap-6" data-id="element-7537" data-mp="3botk2g2vhs3r">
        {/* Thuis */}
        <article aria-label={`Thuis bij ${concern.label.toLowerCase()}`} className="flex flex-col bg-sand p-6 sm:p-8 lg:col-span-7 lg:p-10" data-id="element-7538" data-mp="36smforq4konx">
          <p className="text-[13px] text-muted" data-id="element-7539" data-mp="1cebnqae8hedx">Thuis</p>
          <h3 className="mt-1 type-title text-ink" data-id="element-7540" data-mp="1cfmtgfr8ow7o">{routine.name}</h3>

          <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-7541" data-mp="3chcuhc6i5koq">
            {shown.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-7542" data-mp="3hfo4bmwsrv6y">
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 p-1" data-id="element-7543" data-mp="3mdzczubcvznl" />
                <div className="min-w-0 flex-1" data-id="element-7544" data-mp="3qg2qeohl5ebi">
                  <p className="text-[12px] text-clay" data-id="element-7545" data-mp="3qdq1frdeu3oy">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7546" data-mp="3vd2wv0ickj35">
                    {p.name}
                  </Link>
                </div>
                <span className="shrink-0 text-[14px] tabular-nums text-muted" data-id="element-7547" data-mp="dyxipzoqf9k5">{euro(p.price)}</span>
                <AddButton product={p} data-id="element-7548" data-mp="i0j120k1cg1q" />
              </li>)}
          </ul>
          {more > 0 && <p className="mt-3 text-[14px] text-muted" data-id="element-7549" data-mp="mykm2r10upfu">+ {more} meer in het protocol</p>}

          <div className="mt-auto pt-7" data-id="element-7550" data-mp="27gwiqgfcpjck">
            {!allSoldOut && <button type="button" onClick={() => addRoutine(routine.id)} className={`${btnPrice} sm:w-auto sm:min-w-[20rem]`} data-id="element-7551" data-mp="27f1xf1063p7y">
                <span className={btnPriceLabel} data-id="element-7552" data-mp="1yfyatn1skrop">{fill(addCta)}</span>
                <span className={btnPriceAmount} data-id="element-7553" data-mp="1yib2irxcqgey">{euro(totals.price)}</span>
              </button>}
            {/* Bewijs op het moment van kiezen. */}
            {ratingLine && <p className="mt-3 flex items-center gap-1.5 text-[13px] text-ink" data-id="element-7554" data-mp="2q9qk27k1xcyb">
                <StarIcon className="h-3.5 w-3.5 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-7555" data-mp="2m6uat7geit71" />
                {ratingLine}
              </p>}
            <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-7556" data-mp="2cf6d4ot94fx4">
              Gaat {totals.months} mee · met je huidplan in Mijn omgeving
              {gift ? ` · ${gift.title} ligt voor je klaar` : ''}
              {matching > 0 && <>
                  {' · '}
                  <Link to={`/webshop?huid=${encodeURIComponent(concern.skinType)}`} className="underline underline-offset-4 hover:text-ink" data-id="element-7557" data-mp="2op4e2pgq3vez">
                    alle {plural(matching, 'product', 'producten')} voor {concern.skinType.toLowerCase()}
                  </Link>
                </>}
            </p>
          </div>
        </article>

        {/* In de salon: klei, alleen shell-tekst (5,6:1). Niet uitrekken: geen leeg klei-vlak onderaan. */}
        <article aria-label={`In de salon bij ${concern.label.toLowerCase()}`} className="flex flex-col bg-clay p-6 text-shell sm:p-8 lg:col-span-5 lg:self-start lg:p-10" data-id="element-7558" data-mp="3ceys3acs3q0t">
          <p className="text-[13px]" data-id="element-7559" data-mp="33moh3qu27gii">In de salon</p>
          {treatment ? <>
              <h3 className="mt-1 type-title" data-id="element-7560" data-mp="3rc90uby1ehsu">{treatment.name}</h3>
              {treatment.tagline && <p className="mt-4 text-[17px] leading-relaxed" data-id="element-7561" data-mp="3raeect74ahmn">{treatment.tagline}</p>}
              <dl className="mt-6 grid grid-cols-2 border-y border-shell/25 text-[14px]" data-id="element-7562" data-mp="3w4a9zozwrsen">
                <div className="py-3 pr-4" data-id="element-7563" data-mp="9atvikicl306">
                  <dt className="text-[12px]" data-id="element-7564" data-mp="5b1l029e8ms6">Duur</dt>
                  <dd className="mt-0.5 type-name" data-id="element-7565" data-mp="5ajhckkej6a8">{treatmentDuration(treatment)}</dd>
                </div>
                <div className="border-l border-shell/25 py-3 pl-4" data-id="element-7566" data-mp="j1ey0j156rod">
                  <dt className="text-[12px]" data-id="element-7567" data-mp="e4eo1x1694rz">Prijs</dt>
                  <dd className="mt-0.5 type-name" data-id="element-7568" data-mp="3mix3ijtfzmlm">{treatmentPrice(treatment)}</dd>
                </div>
              </dl>
            </> : <>
              <h3 className="mt-1 type-title" data-id="element-7569" data-mp="3ij4obo9jmi7a">Liever eerst laten kijken?</h3>
              <p className="mt-4 text-[17px] leading-relaxed" data-id="element-7570" data-mp="chy9zmz76yr">
                In een {policy.consult.name.toLowerCase()} van {policy.consult.minutes} minuten kijk ik met je mee en stel ik je plan op.
              </p>
            </>}
          <div className="pt-7" data-id="element-7571" data-mp="16rrbe9f5pgmc">
            <button type="button" onClick={() => openBooking(treatment?.slug ?? policy.consult.slug)} aria-haspopup="dialog" className={quietBook ? `${quietLink} text-shell underline decoration-shell/40 hover:decoration-shell` : btnOnClay} data-id="element-7572" data-mp="16sk7mgghonq8">
              {pageActions.book}
              <span className="sr-only" data-id="element-7573" data-mp="1nr28nym6gvbm">: {treatment ? treatment.name : policy.consult.name}</span>
            </button>
          </div>
        </article>
      </div>
    </div>;
}
function ArrowButton({
  label,
  disabled,
  onClick,
  icon
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  icon: 'prev' | 'next';
}) {
  const Icon = icon === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className={btnIcon} data-id="element-7574" data-mp="151cbx5yutzh2">
      <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7575" data-mp="cehxapopxubi" />
    </button>;
}

/** Passen de pillen niet in hun rij? Dan tonen we vorige/volgende ernaast. */
function usePillOverflow(ref: RefObject<HTMLElement | null>, count: number): boolean {
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, count]);
  return overflow;
}